import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@monksflow/monks-ui';
import { LayoutGridIcon, MessageSquareTextIcon } from 'lucide-react';

import { AudienceInsightsTable } from './components';
import { ResearchAudienceTab } from './ResearchAudienceTab';
import { DataSourcesTab } from './DataSourcesTab';
import type { AudienceInsightsModalProps } from './AudienceInsightsModal.types';
import { useTranslation } from 'react-i18next';

// eslint-disable-next-line i18next/no-literal-string -- tab identifiers, not copy
const TABS = { research: 'research', sources: 'sources' } as const;

type TabValue = (typeof TABS)[keyof typeof TABS];

export function AudienceInsightsModal(props: AudienceInsightsModalProps) {
  const { t } = useTranslation('common');

  const { open, section, subtitle, insights, showPlatformIcons, personaId, category, onClose } =
    props;
  const [activeTab, setActiveTab] = useState<TabValue>(TABS.research);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose?.();
      }}
    >
      <DialogContent className="flex w-[81.25vw] max-w-[81.25vw] flex-col overflow-hidden lg:h-[60vh]">
        <DialogHeader className="gap-0.5">
          <DialogTitle className="text-2xl">
            {t('components.audienceInsightsModal.gwiData', { section })}
          </DialogTitle>
          <DialogDescription>{subtitle}</DialogDescription>
        </DialogHeader>

        <div className="grid flex-1 grid-cols-1 gap-6 lg:min-h-0 lg:grid-cols-2">
          <AudienceInsightsTable insights={insights} showPlatformIcons={showPlatformIcons} />

          {/* Right panel — Research Audience + Data Sources tabs. Renders once a
              trigger passes a personaId; otherwise a placeholder. */}
          {personaId ? (
            /* Long answers/history scroll within the panel instead of growing the modal
               (which would stretch the left table too). `forceMount` keeps both panels
               mounted so an in-flight research request (spinner + state) survives
               switching to Data Sources and back — it does not hide the inactive one,
               so `hidden` is passed explicitly. */
            <Tabs
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as TabValue)}
              className="min-h-0 flex-1 gap-10"
            >
              <TabsList className="w-full">
                <TabsTrigger value={TABS.research} className="flex-1">
                  <LayoutGridIcon />
                  {t('components.audienceInsightsModal.researchAudience')}
                </TabsTrigger>
                <TabsTrigger value={TABS.sources} className="flex-1">
                  <MessageSquareTextIcon />
                  {t('components.audienceInsightsModal.dataSources')}
                </TabsTrigger>
              </TabsList>
              <TabsContent
                value={TABS.research}
                forceMount
                hidden={activeTab !== TABS.research}
                className="min-h-0 overflow-y-auto"
              >
                <ResearchAudienceTab personaId={personaId} category={category} />
              </TabsContent>
              <TabsContent
                value={TABS.sources}
                forceMount
                hidden={activeTab !== TABS.sources}
                className="min-h-0 overflow-y-auto"
              >
                <DataSourcesTab personaId={personaId} category={category} />
              </TabsContent>
            </Tabs>
          ) : (
            <div className="min-h-80 rounded-2xl border border-border lg:min-h-0" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

AudienceInsightsModal.displayName = 'AudienceInsightsModal';
